/**
 * One call site for every admin request.
 *
 * The Worker's admin handlers are still being written, so a route that is not
 * live yet answers 404/501. Rather than each screen growing its own error
 * branch, `call()` retries once against the contract-shaped fixtures in
 * `./mock.ts` and flips a module-level flag that the shell renders as a banner
 * — so it is always obvious on screen whether what you are looking at is real.
 *
 * Responses are parsed with the zod schema from `@exam/shared` in **both**
 * modes, so a fixture that drifts from the contract fails exactly like a
 * backend that drifts from it.
 *
 * When every handler is live: delete `mock.ts`, delete the `fallback` option,
 * and this file becomes a thin typed fetch.
 */
import type { z } from "zod";
import { api, ApiRequestError, isApiError } from "@/lib/api";

export type QueryValue = string | number | boolean | null | undefined;

/**
 * Generic over the schema rather than over its output type: several contract
 * schemas carry `.default()`s, whose input and output types differ, and
 * `z.ZodType<T>` silently collapses the two.
 */
export interface CallOptions<S extends z.ZodTypeAny> {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  path: string;
  query?: Record<string, QueryValue>;
  json?: unknown;
  /** Contract schema — the response is parsed with it, never cast. */
  schema: S;
  /** Contract-shaped fixture used only when the route is not live yet. */
  fallback?: () => z.input<S>;
}

/* ------------------------------------------------------------------ */
/* Fixture-mode flag                                                   */
/* ------------------------------------------------------------------ */

let usingFixtures = false;
const listeners = new Set<(on: boolean) => void>();

export function isUsingFixtures(): boolean {
  return usingFixtures;
}

export function subscribeToFixtureMode(fn: (on: boolean) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function setFixtureMode(on: boolean) {
  if (usingFixtures === on) return;
  usingFixtures = on;
  for (const fn of listeners) fn(on);
}

/**
 * True only when the route itself is not there yet.
 *
 * The distinction that matters: a *live* handler answering "no such attempt"
 * returns a well-formed `ApiErrorBody` with `code: "not_found"`, which
 * `lib/api.ts` surfaces as `code === "not_found"`. An **unmounted** route falls
 * through to Hono's plain-text 404, which cannot be parsed as `ApiErrorBody`
 * and therefore arrives as `code === "internal"`. Only the latter may fall back
 * — otherwise a mistyped id would quietly show sample data for a real attempt.
 */
function isRouteMissing(err: unknown): boolean {
  if (!isApiError(err)) return false;
  if (err.code !== "internal") return false;
  // Unparseable 404/501 = not mounted; 5xx = the stub threw; 0 = Worker down.
  return err.status === 404 || err.status === 501 || err.status >= 500 || err.status === 0;
}

/* ------------------------------------------------------------------ */
/* The call                                                            */
/* ------------------------------------------------------------------ */

export async function call<S extends z.ZodTypeAny>(
  options: CallOptions<S>,
): Promise<z.output<S>> {
  const { method = "GET", path, query, json, schema, fallback } = options;

  try {
    const raw = await api[
      method.toLowerCase() as "get" | "post" | "patch" | "put" | "delete"
    ]<unknown>(path, { query, json });

    const parsed = schema.safeParse(raw);
    if (!parsed.success) {
      // A live route whose shape disagrees with the contract is a real bug —
      // surface it rather than quietly falling back to fixtures.
      throw new ApiRequestError(
        "internal",
        200,
        `${path} did not match its contract: ${parsed.error.issues
          .slice(0, 3)
          .map((i) => `${i.path.join(".")} ${i.message}`)
          .join("; ")}`,
      );
    }
    setFixtureMode(false);
    return parsed.data;
  } catch (err) {
    if (fallback && isRouteMissing(err)) {
      setFixtureMode(true);
      // Parse the fixture too — it must satisfy the same contract.
      return schema.parse(fallback());
    }
    throw err;
  }
}
